import { useEffect, useMemo, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BookingDialog from "@/components/BookingDialog";
import { useAuth } from "@/context/AuthContext";
import {
  addDays,
  startOfWeek,
  fmtDate,
  fmtTime,
  parseISO,
} from "@/lib/dates";
import { format, isSameDay, differenceInMinutes, addMinutes } from "date-fns";
import { da } from "date-fns/locale";
import { Plus, ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 07..20
const HOUR_HEIGHT = 56; // px
const SNAP_MINUTES = 15;

// Assign overlapping bookings to lanes (columns) so they render side-by-side
function assignLanes(items) {
  // items: [{ id, startMin, endMin, ...orig }]
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const laneEnds = []; // laneEnds[i] = end minute of last item in lane i
  const result = [];
  for (const it of sorted) {
    let lane = laneEnds.findIndex((end) => end <= it.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.endMin);
    } else {
      laneEnds[lane] = it.endMin;
    }
    result.push({ ...it, lane });
  }
  // Compute total lanes that overlap each item to size width correctly
  return result.map((it) => {
    let overlapLanes = 1;
    for (const other of result) {
      if (other === it) continue;
      if (other.startMin < it.endMin && it.startMin < other.endMin) {
        overlapLanes = Math.max(overlapLanes, other.lane + 1, it.lane + 1);
      }
    }
    return { ...it, totalLanes: overlapLanes };
  });
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [roomFilter, setRoomFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const dragInfo = useRef(null); // { id, offsetY, duration }
  const [resizePreview, setResizePreview] = useState(null); // { id, start, end }
  const resizeRef = useRef(null); // { id, edge, origStartMs, origEndMs, startY, dateKey }

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const loadStatic = async () => {
    try {
      const [r, c, s] = await Promise.all([
        api.get("/rooms"),
        api.get("/companies"),
        api.get("/settings"),
      ]);
      setRooms(r.data);
      setCompanies(c.data);
      setSettings(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadBookings = async () => {
    try {
      const start = format(weekStart, "yyyy-MM-dd") + "T00:00:00";
      const end = format(addDays(weekStart, 7), "yyyy-MM-dd") + "T00:00:00";
      const params = { start, end };
      if (roomFilter !== "all") params.room_id = roomFilter;
      if (companyFilter !== "all") params.company_id = companyFilter;
      const res = await api.get("/bookings", { params });
      setBookings(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    loadStatic();
  }, []);

  useEffect(() => {
    loadBookings();
    // eslint-disable-next-line
  }, [weekStart, roomFilter, companyFilter]);

  const roomMap = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);

  const handleSlotClick = (day, hour) => {
    const dateStr = format(day, "yyyy-MM-dd");
    const startTime = `${String(hour).padStart(2, "0")}:00`;
    const endTime = `${String(hour + 1).padStart(2, "0")}:00`;
    setEditing({
      room_id: roomFilter !== "all" ? roomFilter : rooms[0]?.id || "",
      start: `${dateStr}T${startTime}:00`,
      end: `${dateStr}T${endTime}:00`,
    });
    setDialogOpen(true);
  };

  const handleBookingClick = (b) => {
    const canEdit = b.user_id === user.id || user.role === "admin";
    if (!canEdit) {
      toast.info(`${b.title} - kun ejeren eller admin kan redigere`);
      return;
    }
    setEditing(b);
    setDialogOpen(true);
  };

  // -------- Drag-and-drop --------
  const handleDragStart = (e, b) => {
    const canEdit = b.user_id === user.id || user.role === "admin";
    if (!canEdit) {
      e.preventDefault();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const duration = differenceInMinutes(parseISO(b.end), parseISO(b.start));
    dragInfo.current = { id: b.id, offsetY, duration, original: b };
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", b.id);
    } catch (_) {}
  };

  const handleDragOver = (e, dayKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverCol !== dayKey) setDragOverCol(dayKey);
  };

  const handleDragLeave = (e, dayKey) => {
    if (dragOverCol === dayKey) setDragOverCol(null);
  };

  const handleDrop = async (e, day) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!dragInfo.current) return;
    const { id, offsetY, duration, original } = dragInfo.current;
    dragInfo.current = null;

    const columnRect = e.currentTarget.getBoundingClientRect();
    const yInCol = e.clientY - columnRect.top - offsetY;
    const minutesFromStart = Math.max(0, Math.round((yInCol / HOUR_HEIGHT) * 60));
    // snap
    const snapped = Math.round(minutesFromStart / SNAP_MINUTES) * SNAP_MINUTES;
    const maxStart = (HOURS.length * 60) - duration;
    const clamped = Math.min(Math.max(snapped, 0), Math.max(maxStart, 0));
    const newStart = addMinutes(new Date(day.getFullYear(), day.getMonth(), day.getDate(), HOURS[0], 0, 0), clamped);
    const newEnd = addMinutes(newStart, duration);

    const isoStart = format(newStart, "yyyy-MM-dd'T'HH:mm:ss");
    const isoEnd = format(newEnd, "yyyy-MM-dd'T'HH:mm:ss");

    if (isoStart === original.start && isoEnd === original.end) return;

    // Optimistic update
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, start: isoStart, end: isoEnd } : b)));
    try {
      await api.put(`/bookings/${id}`, { start: isoStart, end: isoEnd });
      toast.success("Booking flyttet");
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err));
      // Revert
      setBookings((prev) => prev.map((b) => (b.id === id ? original : b)));
    }
  };

  // -------- Resize (top/bottom edge) --------
  const handleResizeStart = (e, b, edge) => {
    e.stopPropagation();
    e.preventDefault();
    const canEdit = b.user_id === user.id || user.role === "admin";
    if (!canEdit) return;
    const minMin = settings?.min_minutes || 15;
    resizeRef.current = {
      id: b.id,
      edge,
      origStartMs: parseISO(b.start).getTime(),
      origEndMs: parseISO(b.end).getTime(),
      startY: e.clientY,
      original: b,
      minMin,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setResizePreview({ id: b.id, start: b.start, end: b.end });
  };

  const handleResizeMove = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    e.preventDefault();
    const deltaY = e.clientY - r.startY;
    const deltaMin = Math.round(((deltaY / HOUR_HEIGHT) * 60) / SNAP_MINUTES) * SNAP_MINUTES;
    let newStart = r.origStartMs;
    let newEnd = r.origEndMs;
    if (r.edge === "bottom") {
      newEnd = r.origEndMs + deltaMin * 60000;
      if ((newEnd - newStart) / 60000 < r.minMin) {
        newEnd = newStart + r.minMin * 60000;
      }
    } else {
      newStart = r.origStartMs + deltaMin * 60000;
      if ((newEnd - newStart) / 60000 < r.minMin) {
        newStart = newEnd - r.minMin * 60000;
      }
    }
    // Clamp within visible hours range on the same day
    const dayBase = new Date(r.origStartMs);
    dayBase.setHours(HOURS[0], 0, 0, 0);
    const dayEnd = new Date(r.origStartMs);
    dayEnd.setHours(HOURS[0] + HOURS.length, 0, 0, 0);
    if (newStart < dayBase.getTime()) newStart = dayBase.getTime();
    if (newEnd > dayEnd.getTime()) newEnd = dayEnd.getTime();

    setResizePreview({
      id: r.id,
      start: format(new Date(newStart), "yyyy-MM-dd'T'HH:mm:ss"),
      end: format(new Date(newEnd), "yyyy-MM-dd'T'HH:mm:ss"),
    });
  };

  const handleResizeEnd = async (e) => {
    const r = resizeRef.current;
    const preview = resizePreview;
    resizeRef.current = null;
    setResizePreview(null);
    if (!r || !preview) return;
    if (preview.start === r.original.start && preview.end === r.original.end) return;

    // Optimistic
    setBookings((prev) => prev.map((b) => (b.id === r.id ? { ...b, start: preview.start, end: preview.end } : b)));
    try {
      await api.put(`/bookings/${r.id}`, { start: preview.start, end: preview.end });
      toast.success("Varighed opdateret");
      loadBookings();
    } catch (err) {
      toast.error(formatApiError(err));
      setBookings((prev) => prev.map((b) => (b.id === r.id ? r.original : b)));
    }
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Kalender</h1>
          <p className="text-slate-500 mt-1">
            Uge {format(weekStart, "w", { locale: da })} · {fmtDate(weekStart)} – {fmtDate(addDays(weekStart, 6))}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="rounded-xl h-11 w-11"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="cal-prev-week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="rounded-xl h-11"
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            data-testid="cal-today"
          >
            <CalIcon className="h-4 w-4 mr-2" />
            I dag
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="rounded-xl h-11 w-11"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            data-testid="cal-next-week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            className="rounded-xl h-11 bg-slate-900 hover:bg-slate-800 gap-2"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            data-testid="new-booking-button"
          >
            <Plus className="h-4 w-4" />
            Ny booking
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={roomFilter} onValueChange={setRoomFilter}>
          <SelectTrigger className="w-[200px] h-10 rounded-xl" data-testid="room-filter">
            <SelectValue placeholder="Alle lokaler" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle lokaler</SelectItem>
            {rooms.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="w-[200px] h-10 rounded-xl" data-testid="company-filter">
            <SelectValue placeholder="Alle virksomheder" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle virksomheder</SelectItem>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="hidden md:flex flex-wrap gap-2 ml-auto">
          <span className="text-xs text-slate-400 self-center mr-1">Tip: træk en booking for at flytte den</span>
          {rooms.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-xs px-3 py-1.5 bg-white rounded-full border border-slate-200">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
              {r.name}
            </div>
          ))}
        </div>
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header */}
            <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-200 bg-slate-50">
              <div />
              {days.map((d) => {
                const isToday = isSameDay(d, new Date());
                return (
                  <div
                    key={d.toISOString()}
                    className={`px-3 py-3 text-center ${isToday ? "bg-slate-900 text-white" : ""}`}
                  >
                    <div className="text-xs font-medium uppercase tracking-wider opacity-70">
                      {format(d, "EEE", { locale: da })}
                    </div>
                    <div className="text-lg font-semibold font-display">
                      {format(d, "d/M")}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Body */}
            <div className="grid grid-cols-[60px_repeat(7,1fr)] relative">
              {/* Hour labels */}
              <div className="border-r border-slate-200">
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="text-xs text-slate-400 text-right pr-2 pt-1"
                    style={{ height: HOUR_HEIGHT }}
                  >
                    {String(h).padStart(2, "0")}:00
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {days.map((day) => {
                const dayKey = day.toISOString();
                const rawDayBookings = bookings.filter((b) =>
                  isSameDay(parseISO(b.start), day),
                );
                // Compute lane layout for side-by-side overlapping bookings
                const laidOut = assignLanes(
                  rawDayBookings.map((b) => {
                    const s = parseISO(b.start);
                    const e = parseISO(b.end);
                    return {
                      booking: b,
                      startMin: s.getHours() * 60 + s.getMinutes(),
                      endMin: e.getHours() * 60 + e.getMinutes(),
                    };
                  }),
                );
                return (
                  <div
                    key={dayKey}
                    className={`relative border-r border-slate-100 last:border-r-0 transition-colors ${
                      dragOverCol === dayKey ? "bg-slate-100/60" : ""
                    }`}
                    style={{ height: HOURS.length * HOUR_HEIGHT }}
                    onDragOver={(e) => handleDragOver(e, dayKey)}
                    onDragLeave={(e) => handleDragLeave(e, dayKey)}
                    onDrop={(e) => handleDrop(e, day)}
                    data-testid={`day-column-${format(day, "yyyy-MM-dd")}`}
                  >
                    {HOURS.map((h) => (
                      <button
                        type="button"
                        key={h}
                        className="block w-full border-b border-slate-100 hover:bg-slate-50/80 transition-colors"
                        style={{ height: HOUR_HEIGHT }}
                        onClick={() => handleSlotClick(day, h)}
                        data-testid={`slot-${format(day, "yyyy-MM-dd")}-${h}`}
                        aria-label={`Book ${format(day, "EEE d/M", { locale: da })} kl ${h}`}
                      />
                    ))}
                    {laidOut.map(({ booking: bRaw, lane, totalLanes }) => {
                      // Apply live preview during resize
                      const b = resizePreview && resizePreview.id === bRaw.id
                        ? { ...bRaw, start: resizePreview.start, end: resizePreview.end }
                        : bRaw;
                      const start = parseISO(b.start);
                      const end = parseISO(b.end);
                      const startH = start.getHours() + start.getMinutes() / 60;
                      const top = (startH - HOURS[0]) * HOUR_HEIGHT;
                      const height = Math.max(
                        differenceInMinutes(end, start) * (HOUR_HEIGHT / 60),
                        28,
                      );
                      const room = roomMap[b.room_id];
                      const color = room?.color || "#3B82F6";
                      const isMine = b.user_id === user.id;
                      const canEdit = isMine || user.role === "admin";
                      const widthPct = 100 / totalLanes;
                      const leftPct = lane * widthPct;
                      const isResizing = resizePreview && resizePreview.id === b.id;
                      return (
                        <div
                          key={b.id}
                          draggable={canEdit && !isResizing}
                          onDragStart={(e) => handleDragStart(e, b)}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isResizing) return;
                            handleBookingClick(b);
                          }}
                          className={`absolute rounded-lg text-left text-white shadow-sm hover:shadow-md hover:z-10 transition-shadow overflow-hidden group ${
                            canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                          } ${isResizing ? "ring-2 ring-white/70 z-20" : ""}`}
                          style={{
                            top: Math.max(top, 0),
                            height,
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`,
                            background: color,
                            opacity: b.status === "cancelled" ? 0.4 : 1,
                          }}
                          data-testid={`booking-${b.id}`}
                          title={`${b.title} · ${room?.name || ""} · ${fmtTime(b.start)}-${fmtTime(b.end)}${b.group_id ? " · serie" : ""}`}
                        >
                          {/* Top resize handle */}
                          {canEdit && (
                            <div
                              onPointerDown={(e) => handleResizeStart(e, b, "top")}
                              onPointerMove={handleResizeMove}
                              onPointerUp={handleResizeEnd}
                              onPointerCancel={handleResizeEnd}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-white/30 transition-colors z-10"
                              data-testid={`resize-top-${b.id}`}
                              title="Træk for at justere starttid"
                            />
                          )}
                          <div className="p-2">
                            <div className="text-[11px] font-semibold leading-tight">
                              {fmtTime(b.start)}–{fmtTime(b.end)}
                            </div>
                            <div className="text-xs font-medium truncate">{b.title}</div>
                            <div className="text-[10px] opacity-90 truncate">{room?.name}</div>
                          </div>
                          <div className="absolute top-1 right-1 flex gap-1 pointer-events-none">
                            {b.group_id && (
                              <span className="text-[9px] leading-none px-1 py-0.5 bg-white/30 rounded">↻</span>
                            )}
                            {isMine && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          {/* Bottom resize handle */}
                          {canEdit && (
                            <div
                              onPointerDown={(e) => handleResizeStart(e, b, "bottom")}
                              onPointerMove={handleResizeMove}
                              onPointerUp={handleResizeEnd}
                              onPointerCancel={handleResizeEnd}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-white/30 transition-colors z-10"
                              data-testid={`resize-bottom-${b.id}`}
                              title="Træk for at justere sluttid"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      {dialogOpen && (
        <BookingDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
          onSaved={loadBookings}
          rooms={rooms.filter((r) => r.active)}
          initial={editing}
          allowRecurring={settings?.allow_recurring ?? true}
        />
      )}
    </div>
  );
}
